import { useSearchParams } from "react-router-dom"
import { SchedulesQueryParams, EmployeeTabs, LocationTabs, ScheduleTabs } from "types/schedules.types"
import { useMemo, useCallback } from "react"

/**
 * useSchedulesQueryParams
 *
 * A consolidated hook to manage all URL-driven state for the Schedules, Employees, and Locations modules.
 * It provides:
 * 1. Raw access to params.
 * 2. Pre-renamed "selected" aliases for common parameters.
 * 3. Semantic actions (setters) that handle logic/dependencies (e.g., clearing location when branch changes).
 */
const useSchedulesQueryParams = () => {
	const [searchParams, setSearchParams] = useSearchParams()

	const params = useMemo(() => {
		return Object.fromEntries(searchParams.entries()) as Record<string, string> as Partial<SchedulesQueryParams>
	}, [searchParams])

	const {
		location_branch,
		employee_branch,
		customer,
		supplier,
		location,
		employee,
		tab,
		contract,
		date,
		schedule,
		selected,
		activeOnly,
	} = params

	const setSearchParamsWithReplace = useCallback(
		(newParams: Partial<SchedulesQueryParams>) => {
			//const combined = { ...params, ...newParams }
			const {
				tab = "",
				location_branch = "",
				employee_branch = "",
				customer = "",
				supplier = "",
				location = "",
				employee = "",
				contract = "",
				date = "",
				schedule = "",
				selected = "",
				activeOnly = "true",
			} = { ...params, ...newParams }
			setSearchParams(
				{
					...(!!location_branch && { location_branch }),
					...(!!employee_branch && { employee_branch }),
					...(!!customer && { customer }),
					...(!!supplier && { supplier }),
					...(!!tab && { tab }),
					...(!!contract && { contract }),
					...(!!date && { date }),
					...(!!location && { location }),
					...(!!employee && { employee }),
					...(!!schedule && { schedule }),
					...(!!selected && { selected }),
					...(!!activeOnly && { activeOnly }),
				},
				{ replace: true },
			)
		},
		[searchParams, setSearchParams],
	)

	const setSearchParamsWithHistory = useCallback(
		(newParams: Partial<SchedulesQueryParams>) => {
			const combined = { ...params, ...newParams }

			// Filter out empty values
			const cleanParams: Record<string, string> = {}
			Object.entries(combined).forEach(([key, value]) => {
				if (value) cleanParams[key] = value
			})

			setSearchParams(cleanParams, { replace: false })
		},
		[searchParams, setSearchParams],
	)

	// --- Semantic Actions ---

	const selectLocationBranch = useCallback(
		(branchId: string) => {
			setSearchParamsWithReplace({
				location_branch: branchId || "",
				location: "", // Semantic: Clear location when branch changes
			})
		},
		[setSearchParamsWithReplace],
	)

	const selectEmployeeBranch = useCallback(
		(branchId: string) => {
			setSearchParamsWithReplace({
				employee_branch: branchId || "",
				supplier: "", // Clear supplier if switching to branch view
				employee: "", // Clear specific employee
			})
		},
		[setSearchParamsWithReplace],
	)

	const selectCustomer = useCallback(
		(customerId: string) => {
			setSearchParamsWithReplace({
				customer: customerId || "",
				location: "", // Semantic: Clear location when customer changes
			})
		},
		[setSearchParamsWithReplace],
	)

	const selectSupplier = useCallback(
		(supplierId: string) => {
			setSearchParamsWithReplace({
				supplier: supplierId || "",
				employee_branch: "", // Clear branch if switching to supplier view
				employee: "", // Clear specific employee
			})
		},
		[setSearchParamsWithReplace],
	)

	const selectLocation = useCallback(
		(locationId: string, tab?: LocationTabs) => {
			setSearchParamsWithReplace({
				location: locationId || "",
				...(tab && { tab }),
			})
		},
		[setSearchParamsWithReplace],
	)

	const selectEmployee = useCallback(
		(employeeId: string, tab?: ScheduleTabs | EmployeeTabs) => {
			setSearchParamsWithReplace({
				employee: employeeId || "",
				...(tab && { tab }),
			})
		},
		[setSearchParamsWithReplace],
	)

	const selectTab = useCallback(
		(tab: ScheduleTabs | EmployeeTabs | LocationTabs) => {
			setSearchParamsWithReplace({ tab })
		},
		[setSearchParamsWithReplace],
	)

	const selectContract = useCallback(
		(contractId: string) => {
			setSearchParamsWithReplace({ contract: contractId || "" })
		},
		[setSearchParamsWithReplace],
	)

	const selectDate = useCallback(
		(date: string) => {
			setSearchParamsWithReplace({ date: date || "" })
		},
		[setSearchParamsWithReplace],
	)

	const selectSchedule = useCallback(
		(scheduleKey: string) => {
			setSearchParamsWithReplace({ schedule: scheduleKey || "" })
		},
		[setSearchParamsWithReplace],
	)

	const selectItem = useCallback(
		(itemId: string) => {
			setSearchParamsWithReplace({ selected: itemId || "" })
		},
		[setSearchParamsWithReplace],
	)

	const selectActiveOnly = useCallback(
		(activeOnly: boolean) => {
			setSearchParamsWithReplace({ activeOnly: activeOnly ? "true" : "false", location: "" })
		},
		[setSearchParamsWithReplace],
	)

	return {
		tab,
		contract,
		date,
		schedule,
		selected,
		activeOnly,
		// Global State
		selectedLocationBranch: location_branch,
		selectedEmployeeBranch: employee_branch,
		selectedCustomer: customer,
		selectedSupplier: supplier,
		selectedLocation: location,
		selectedEmployee: employee,

		// Actions
		setSearchParams: setSearchParamsWithReplace,
		setSearchParamsWithHistory,
		selectLocationBranch,
		selectEmployeeBranch,
		selectCustomer,
		selectSupplier,
		selectLocation,
		selectEmployee,
		selectTab,
		selectContract,
		selectDate,
		selectSchedule,
		selectItem,
		selectActiveOnly,
	}
}

export default useSchedulesQueryParams
